import { useEffect, useRef, useState } from "react";
import i18n from "@/utils/i18n";
import {
  decodeJwtPayload,
  extractIsModeratorFromToken,
  extractJitsiUserFromToken,
  TOOLBAR_BUTTONS_NO_WHITEBOARD,
  type JitsiUserInfo,
} from "@/lib/jitsi-jwt";

export { decodeJwtPayload, extractJitsiUserFromToken };

interface JitsiEmbedProps {
  /** Backend'den gelen tam Jitsi URL'i (örn. https://meet.jit.si/euas-xxx). */
  externalUrl: string;
  /** İsteğe bağlı JWT — sadece public domain'de değilse iletilir. */
  token?: string | null;
  /** Kullanıcı kimliğini Jitsi'ye geç (prejoin formu doldurulmasın diye). */
  user?: JitsiUserInfo;
  /** Kullanıcı toplantıdan ayrıldığında / kapattığında tetiklenir. */
  onLeave?: () => void;
  className?: string;
}

// Window.JitsiMeetExternalAPI tek tanım: src/types/jitsi.d.ts
type JitsiApi = JitsiMeetExternalAPIInstance;

const SCRIPT_ID = "jitsi-external-api-script";
// Public meet.jit.si JWT validasyonu yapıyor; backend'in ürettiği token
// `iss` boş olduğu için reddediliyor. Bu domain'lerde JWT'yi atla.
const PUBLIC_JITSI_DOMAINS = new Set(["meet.jit.si", "8x8.vc"]);

// Jitsi external_api ve nginx HTTPS üzerinden serve edilir (lokal dev'de
// self-signed cert, prod'da Let's Encrypt). Tek protokol: HTTPS.

function loadJitsiScript(domain: string): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.JitsiMeetExternalAPI) {
    return Promise.resolve();
  }
  const existing = document.getElementById(
    SCRIPT_ID,
  ) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error(i18n.t("jitsiEmbed.loadFailed"))),
      );
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://${domain}/external_api.js`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(i18n.t("jitsiEmbed.loadFailed")));
    document.head.appendChild(s);
  });
}

function parseJitsiUrl(
  url: string,
): { domain: string; roomName: string } | null {
  try {
    const u = new URL(url);
    const room = u.pathname.replace(/^\//, "").split("/")[0];
    if (!room) return null;
    return { domain: u.host, roomName: decodeURIComponent(room) };
  } catch {
    return null;
  }
}

type LoadStatus = "loading" | "ready" | "error" | "invalid";

export function JitsiEmbed(props: JitsiEmbedProps) {
  const { externalUrl, token, user, onLeave, className } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useRef<JitsiApi | null>(null);
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;
  const [status, setStatus] = useState<LoadStatus>("loading");

  useEffect(() => {
    const parsed = parseJitsiUrl(externalUrl);
    if (!parsed) {
      setStatus("invalid");
      return;
    }
    let cancelled = false;
    const sendJwt = !PUBLIC_JITSI_DOMAINS.has(parsed.domain);
    const isModerator = extractIsModeratorFromToken(token);
    setStatus("loading");

    void loadJitsiScript(parsed.domain)
      .then(() => {
        if (cancelled) return;
        const Ctor = window.JitsiMeetExternalAPI;
        if (!containerRef.current || !Ctor) {
          setStatus("error");
          return;
        }
        // Önceki instance kalmışsa temizle (StrictMode / hot reload).
        if (apiRef.current) {
          try {
            apiRef.current.dispose();
          } catch {
            // ignore
          }
          apiRef.current = null;
        }
        const api = new Ctor(parsed.domain, {
          roomName: parsed.roomName,
          parentNode: containerRef.current,
          width: "100%",
          height: "100%",
          ...(sendJwt && token ? { jwt: token } : {}),
          ...(user && (user.displayName || user.email)
            ? {
                userInfo: {
                  ...(user.displayName ? { displayName: user.displayName } : {}),
                  ...(user.email ? { email: user.email } : {}),
                },
              }
            : {}),
          configOverwrite: {
            prejoinPageEnabled: false,
            prejoinConfig: { enabled: false },
            disableDeepLinking: true,
            startWithAudioMuted: true,
          },
          interfaceConfigOverwrite: {
            MOBILE_APP_PROMO: false,
            SHOW_JITSI_WATERMARK: false,
            SHOW_PROMOTIONAL_CLOSE_PAGE: false,
            // Sadece moderator (eğitmen) whiteboard butonunu görür.
            // Kursiyer için TOOLBAR_BUTTONS override → whiteboard listede yok.
            ...(isModerator ? {} : { TOOLBAR_BUTTONS: TOOLBAR_BUTTONS_NO_WHITEBOARD }),
          },
        });
        apiRef.current = api;
        setStatus("ready");
        const handleLeft = () => {
          onLeaveRef.current?.();
        };
        try {
          api.addListener?.("readyToClose", handleLeft);
          api.addListener?.("videoConferenceLeft", handleLeft);
        } catch {
          // ignore
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
      if (apiRef.current) {
        try {
          apiRef.current.dispose();
        } catch {
          // ignore
        }
        apiRef.current = null;
      }
    };
  }, [externalUrl, token, user?.displayName, user?.email]);

  return (
    <div className={className} style={{ position: "relative" }}>
      <div
        ref={containerRef}
        style={{ width: "100%", height: "100%" }}
      />
      {status !== "ready" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            background: "rgba(0,0,0,0.7)",
            borderRadius: 6,
            textAlign: "center",
            padding: 16,
            fontSize: 14,
          }}
        >
          {status === "loading" && i18n.t("jitsiEmbed.loading")}
          {status === "error" &&
            i18n.t("jitsiEmbed.loadError")}
          {status === "invalid" && i18n.t("jitsiEmbed.invalid")}
        </div>
      )}
    </div>
  );
}

